import React, { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Mail, Lock, LogIn, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { LOGO_MARK, TAGLINE } from "../lib/brand";
import { LogoMark } from "../components/Logo";

export default function Login() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nwrEnabled, setNwrEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => setNwrEnabled(!!r.data?.auth0_enabled)).catch(() => {});
  }, []);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Google OAuth is not available in standalone mode.
  // Use NWR ID (Auth0) for OAuth login instead, or email/password.

  const nwrLogin = async () => {
    try {
      const redirect_uri = `${window.location.origin}/auth/nwr-callback`;
      const { data } = await api.get("/auth/nwr/login", { params: { redirect_uri } });
      window.location.href = data.auth_url;
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-8" data-testid="logo-link">
          <LogoMark src={LOGO_MARK} size="w-16 h-16" className="animate-float" />
          <span className="brand-text text-2xl">NORDGAMING</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{TAGLINE}</span>
        </Link>

        <div className="card p-8 fade-in tracing-border">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-3">01 · Login</div>
          <h1 className="font-display text-3xl tracking-tight mb-2">Willkommen zurück</h1>
          <p className="text-white/60 text-sm mb-8">Melde dich mit deinem Mitgliedskonto an.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label">E-Mail</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="du@nordgaming.de"
                  className="input pl-10"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label className="label">Passwort</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input pl-10"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
              </div>
            </div>
            {error && (
              <div
                className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 p-3 rounded-sm font-mono"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full"
              data-testid="login-submit-button"
            >
              {submitting ? "Anmelden…" : (
                <>
                  <LogIn className="w-4 h-4" />
                  Login
                </>
              )}
            </button>
          </form>

          {nwrEnabled && (
            <>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">oder</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <button
                onClick={nwrLogin}
                className="btn-secondary w-full"
                data-testid="nwr-login-button"
              >
                <ShieldCheck className="w-4 h-4" />
                Login mit NWR ID
              </button>
            </>
          )}

          <div className="mt-6 text-sm text-white/60 text-center">
            Noch kein Mitglied?{" "}
            <Link to="/register" className="text-primary link-underline" data-testid="go-to-register">
              Jetzt beitreten
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-white/40 text-xs font-mono uppercase tracking-[0.2em] hover:text-primary" data-testid="back-home">
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}
